import React, { useState, useEffect } from 'react'
import AccountTypeSelector from './AccountTypeSelector'

interface Supplier {
  id: string
  name: string
}

interface SupplierPayment {
  id: string
  number?: string
  date: string
  supplierId: string
  amount: number
  status: 'DRAFT' | 'POSTED' | 'CANCELLED'
  comment?: string
  accountId?: string
  supplier?: Supplier
}

interface Props {
  title: string
  suppliers: Supplier[]
  initial?: SupplierPayment | null
  onCancel: () => void
  onSubmit: (data: Partial<SupplierPayment>) => Promise<void>
}

const SupplierPaymentForm: React.FC<Props> = ({
  title,
  suppliers,
  initial,
  onCancel,
  onSubmit
}) => {
  const [formData, setFormData] = useState({
    date: initial?.date ? new Date(initial.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
    supplierId: initial?.supplierId || '',
    amount: initial?.amount || 0,
    status: initial?.status || 'DRAFT' as 'DRAFT' | 'POSTED' | 'CANCELLED',
    comment: initial?.comment || '',
    accountId: initial?.accountId || ''
  })

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  // Обновляем formData при изменении initial
  useEffect(() => {
    if (initial) {
      setFormData({
        date: initial.date ? new Date(initial.date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
        supplierId: initial.supplierId || '',
        amount: initial.amount || 0,
        status: initial.status || 'DRAFT',
        comment: initial.comment || '',
        accountId: initial.accountId || ''
      })
    }
  }, [initial])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    try {
      await onSubmit(formData)
    } catch (err: any) {
      setError(err.message || 'Ошибка сохранения')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black opacity-30" onClick={onCancel} />
      <div className="relative bg-white rounded-lg shadow-lg border w-full max-w-md mx-4">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button className="text-gray-500 hover:text-gray-700" onClick={onCancel}>✕</button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-md border border-red-200">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Дата *
            </label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => handleChange('date', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Поставщик *
            </label>
            <select
              value={formData.supplierId}
              onChange={(e) => handleChange('supplierId', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Выберите поставщика</option>
              {suppliers.map(supplier => (
                <option key={supplier.id} value={supplier.id}>
                  {supplier.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Сумма *
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={formData.amount}
              onChange={(e) => handleChange('amount', parseFloat(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>


          <div>
            <AccountTypeSelector
              value={formData.accountId}
              onChange={(accountId) => handleChange('accountId', accountId)}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Статус *
            </label>
            <select
              value={formData.status}
              onChange={(e) => handleChange('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="DRAFT">Черновик</option>
              <option value="POSTED">Проведен</option>
              <option value="CANCELLED">Отменен</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Комментарий
            </label>
            <textarea
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? 'Сохранение...' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default SupplierPaymentForm
